import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const Steam = () => (
  <>
    {/* Full-screen drifting smoke layer */}
    <div className="absolute inset-0 z-20 pointer-events-none overflow-hidden">
      {Array.from({ length: 9 }).map((_, i) => (
        <div
          key={`smoke-${i}`}
          className="smoke"
          style={{
            left: `${-10 + i * 13}%`,
            bottom: `${-20 + (i % 3) * 18}%`,
            animationDelay: `${i * 1.6}s`,
            animationDuration: `${14 + (i % 5) * 3}s`,
          }}
        />
      ))}
    </div>
    {/* Dense rising steam from the floor */}
    <div className="absolute inset-x-0 bottom-0 h-3/4 z-20 pointer-events-none overflow-hidden">
      {Array.from({ length: 18 }).map((_, i) => (
        <div
          key={`steam-${i}`}
          className="steam"
          style={{
            left: `${2 + i * 5.4}%`,
            animationDelay: `${(i % 9) * 0.7}s`,
            animationDuration: `${5 + (i % 4)}s`,
          }}
        />
      ))}
    </div>
  </>
);

export const Hero = ({ heroImage }) => {
  const [opened, setOpened] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setOpened(true), 600);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (v) { v.muted = true; v.play().catch(() => {}); }
  }, []);

  return (
    <section className="relative h-[100svh] min-h-[600px] overflow-hidden bg-espresso" data-testid="hero">
      {/* Background video (looping) with image poster fallback */}
      <div className="absolute inset-0">
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          autoPlay muted loop playsInline preload="auto"
          poster={heroImage}
          data-testid="hero-video"
        >
          <source
            src="https://customer-assets.emergentagent.com/job_luxury-wellness-shop/artifacts/iku74qmw_hf_20260626_004133_83dbad8c-0d6f-4185-ac99-1470af28b5ba%20%281%29.mp4"
            type="video/mp4"
          />
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-espresso via-espresso/40 to-espresso/30" />
      </div>

      <Steam />

      {/* Door panels */}
      <motion.div
        initial={{ x: 0 }}
        animate={{ x: opened ? "-100%" : 0 }}
        transition={{ duration: 2.2, ease: [0.76, 0, 0.24, 1] }}
        className="absolute inset-y-0 left-0 w-1/2 z-30 origin-left"
        style={{
          background: "linear-gradient(100deg,#241c12,#3a2c1a 60%,#1a130b)",
          boxShadow: "inset -30px 0 60px rgba(0,0,0,0.7)",
          borderRight: "2px solid rgba(212,168,67,0.3)",
        }}
      >
        <div className="absolute right-4 top-1/2 -translate-y-1/2 w-2 h-24 bg-gold/40 rounded" />
      </motion.div>
      <motion.div
        initial={{ x: 0 }}
        animate={{ x: opened ? "100%" : 0 }}
        transition={{ duration: 2.2, ease: [0.76, 0, 0.24, 1] }}
        className="absolute inset-y-0 right-0 w-1/2 z-30 origin-right"
        style={{
          background: "linear-gradient(260deg,#241c12,#3a2c1a 60%,#1a130b)",
          boxShadow: "inset 30px 0 60px rgba(0,0,0,0.7)",
          borderLeft: "2px solid rgba(212,168,67,0.3)",
        }}
      >
        <div className="absolute left-4 top-1/2 -translate-y-1/2 w-2 h-24 bg-gold/40 rounded" />
      </motion.div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-5">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: opened ? 1 : 0 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="overline mb-5"
        >
          Saunas · Cold Plunges · Hot Tubs
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: opened ? 1 : 0, y: opened ? 0 : 30 }}
          transition={{ delay: 1.8, duration: 1.1 }}
          className="font-serif text-4xl sm:text-6xl lg:text-7xl text-cream max-w-4xl leading-[1.05] tracking-tight"
        >
          Elite Recovery<br />Starts Here.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: opened ? 1 : 0 }}
          transition={{ delay: 2.2, duration: 1 }}
          className="text-cream/75 mt-6 max-w-xl text-base sm:text-lg font-light"
        >
          A guided journey to your perfect wellness sanctuary — handpicked saunas, cold plunges and hot tubs, delivered white glove.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: opened ? 1 : 0, y: opened ? 0 : 20 }}
          transition={{ delay: 2.5, duration: 1 }}
          className="flex flex-col sm:flex-row gap-4 mt-9"
        >
          <Link to="/quiz" data-testid="hero-quiz-cta"
            className="group bg-gold text-espresso px-8 py-3.5 font-medium flex items-center justify-center gap-2 hover:bg-amber transition-colors">
            Find Your Match
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link to="/shop" data-testid="hero-shop-cta"
            className="border border-gold/60 text-gold px-8 py-3.5 font-medium hover:bg-gold/10 transition-colors">
            Shop All
          </Link>
          <Link to="/commercial" data-testid="hero-commercial-cta"
            className="border border-cream/30 text-cream px-8 py-3.5 font-medium hover:border-cream transition-colors">
            Commercial Solutions
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
